import type { SessionEvent } from "../core/types"

function now(): string {
  return new Date().toISOString()
}

// Route patterns whose dynamic segments collapse into a single canonical path.
// A segment prefixed with ":" matches any value. Configured via Relia.init({ routePatterns }).
let configuredRoutePatterns: string[] | undefined

export function setRoutePatterns(patterns: string[] | undefined): void {
  configuredRoutePatterns = patterns && patterns.length > 0 ? patterns : undefined
}

function normalizePath(pathname: string): string {
  if (!configuredRoutePatterns) return pathname
  for (const pattern of configuredRoutePatterns) {
    const pSeg = pattern.split("/").filter(Boolean)
    const aSeg = pathname.split("/").filter(Boolean)
    if (pSeg.length !== aSeg.length) continue
    let matched = true
    for (let i = 0; i < pSeg.length; i++) {
      if (pSeg[i].startsWith(":")) continue
      if (pSeg[i] !== aSeg[i]) {
        matched = false
        break
      }
    }
    if (matched) return pattern
  }
  return pathname
}

// Centralized current-path reader — applies route-pattern normalization so that
// journey events group dynamic-id routes (e.g. /products/:id) into one flow.
export function currentPath(): string {
  const raw = typeof window !== "undefined" ? window.location.pathname : "/"
  return normalizePath(raw)
}

// Regex patterns for sensitive PII and user contact details (IP addresses are preserved)
const CC_REGEX = /\b(?:\d[ -]*?){13,16}\b/g
const SSN_REGEX = /\b\d{3}-\d{2}-\d{4}\b/g
export const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g
export const PHONE_REGEX = /(?<!\d\.)(?:\+\d{1,3}[- ]?)?(?:\([0-9]{2,4}\)[- ]?|[0-9]{3}[- ])[0-9]{3}[- ][0-9]{4}\b|(?<!\d\.)(?:\+\d{1,3}[- ]?)?\b\d{10,12}\b/g
export const STREET_REGEX = /\b\d{1,5}\s+[A-Za-z0-9\s.,]{3,35}\s+(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Lane|Ln|Drive|Dr|Court|Ct|Circle|Cir|Way|Highway|Hwy|Place|Pl|Apartment|Apt|Suite)\b/gi

export const COMMON_USER_NAME = "Demo User"
export const COMMON_USER_EMAIL = "user@example.com"
export const COMMON_USER_ID = "usr_demo"
export const COMMON_PHONE = "555-0100"
export const COMMON_ADDRESS = "123 Main St"

export function sanitizeText(text: string, isMasked = false): string {
  if (!text) return ""
  if (isMasked) return "••• [MASKED] •••"
  return text
    .replace(EMAIL_REGEX, COMMON_USER_EMAIL)
    .replace(STREET_REGEX, COMMON_ADDRESS)
    .replace(PHONE_REGEX, COMMON_PHONE)
    .replace(CC_REGEX, "••••-••••-••••-••••")
    .replace(SSN_REGEX, "•••-••-••••")
    .replace(/\s+/g, " ")
    .trim()
}

export function isBlockedElement(el: Element | null): boolean {
  if (!el) return false
  return Boolean(
    el.closest?.(".relia-block, [data-relia-block], .relia-ignore, [data-relia-ignore]")
  )
}

export function isMaskedElement(el: Element | null): boolean {
  if (!el) return false
  return Boolean(
    el.closest?.(".relia-mask, [data-relia-mask], [data-relia-sensitive]")
  )
}

export function isElementInteractive(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (["button", "a", "input", "select", "textarea", "form"].includes(tag)) return true
  const role = el.getAttribute("role") || ""
  if (role && ["button", "link", "checkbox", "radio", "tab", "menuitem", "switch", "option"].includes(role)) return true
  if (el.getAttribute("onclick") || el.getAttribute("data-action")) return true
  if (el.getAttribute("tabindex") && el.getAttribute("tabindex") !== "-1") return true
  if (typeof window !== "undefined") {
    try {
      const style = window.getComputedStyle(el)
      if (style.cursor === "pointer") return true
    } catch {}
  }
  return false
}

export function findInteractiveElement(el: Element | null): Element | null {
  if (!el) return null
  const standard = el.closest(
    "button, a, input, select, textarea, [role='button'], [role='link'], [role='tab'], [role='menuitem'], [role='checkbox'], [role='switch'], [role='option'], [tabindex], [onclick], [data-action], form"
  )
  if (standard) return standard

  // Check computed cursor: pointer upward
  let curr: Element | null = el
  while (curr && curr !== document.body && curr !== document.documentElement) {
    if (isElementInteractive(curr)) return curr
    curr = curr.parentElement
  }
  return el
}

function elementLabel(rawEl: Element): string {
  if (isBlockedElement(rawEl)) return "[blocked]"

  const el = findInteractiveElement(rawEl) || rawEl
  const isMasked = isMaskedElement(el)
  const tag = el.tagName.toLowerCase()
  const id = el.id && !isMasked ? `#${el.id}` : ""

  // Try aria-label or title or name first for high semantic clarity
  const aria = (!isMasked && (el.getAttribute("aria-label") || el.getAttribute("title") || el.getAttribute("name"))) || ""
  if (aria) {
    return `${tag}${id}[${aria.slice(0, 40)}]`
  }

  // If link, include sanitized href
  if (tag === "a" && el.getAttribute("href")) {
    const href = el.getAttribute("href") || ""
    return `a[href="${href.split("?")[0].slice(0, 40)}"]`
  }

  const classes = Array.from(el.classList)
    .filter((c) => !c.startsWith("hover:") && !c.startsWith("focus:") && !c.startsWith("dark:"))
    .slice(0, 2)
    .map((c) => `.${c}`)
    .join("")

  const rawText = (el.textContent || "").trim()
  const text = isMasked ? "•••" : sanitizeText(rawText).slice(0, 40)
  return (text ? `${tag}: ${text}` : `${tag}${id}${classes}`).slice(0, 100)
}

function base(el: Element | null | undefined): { target: string; path: string } {
  return {
    target: el instanceof Element ? elementLabel(el) : "",
    path: currentPath(),
  }
}

export function isElementDisabled(el: Element | null): boolean {
  if (!el) return false
  if ((el as HTMLButtonElement).disabled) return true
  if (el.getAttribute("aria-disabled") === "true") return true
  if (el.closest?.("[disabled], [aria-disabled='true']")) return true
  return false
}

export function isElementVisible(el: Element | null): boolean {
  if (!el) return false
  try {
    const rect = el.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  } catch {
    return true
  }
}

export function captureClick(el: Element | null, captureText: boolean): SessionEvent {
  if (isBlockedElement(el)) {
    return {
      type: "click",
      ts: now(),
      target: "[blocked]",
      path: currentPath(),
      text: "",
      meta: { blocked: true },
    }
  }

  const interactive = findInteractiveElement(el) || el
  const isMasked = isMaskedElement(interactive)
  const { target, path } = base(interactive)
  const rawText = interactive?.textContent || el?.textContent || ""
  const text = captureText ? sanitizeText(rawText, isMasked).slice(0, 150) : ""
  const role = interactive?.getAttribute?.("role") || ""
  const isDisabled = isElementDisabled(interactive)
  const isVisible = isElementVisible(interactive)
  const isExternal = (interactive as HTMLAnchorElement)?.target === "_blank"
  const isInteractive = isElementInteractive(interactive) && !isDisabled && !isExternal

  return {
    type: "click",
    ts: now(),
    target,
    path,
    text,
    meta: {
      tag: interactive?.tagName?.toLowerCase() || "",
      role,
      is_interactive: isInteractive,
      is_disabled: isDisabled,
      is_visible: isVisible,
      is_external: isExternal,
      href: (interactive as HTMLAnchorElement)?.href?.split("?")[0] || "",
    },
  }
}

export function captureInput(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  _captureText: boolean,
): SessionEvent {
  if (isBlockedElement(el)) {
    return {
      type: "input",
      ts: now(),
      target: "[blocked]",
      path: currentPath(),
      text: "",
      meta: { blocked: true },
    }
  }

  const { target, path } = base(el)
  const isMasked = isMaskedElement(el)
  const isPassword = el?.type === "password" || el?.autocomplete?.includes("cc-") || el?.autocomplete?.includes("password")
  const value = el?.value ?? ""
  
  // Blur and mask input text: never capture raw keystrokes or sensitive values
  const text = `${value.length} chars`

  return {
    type: "input",
    ts: now(),
    target,
    path,
    text,
    meta: {
      input_type: el?.type ?? "text",
      input_name: el?.name ?? "",
      input_id: el?.id ?? "",
      is_masked: true,
    },
  }
}

export function captureChange(el: HTMLSelectElement | HTMLInputElement | null): SessionEvent {
  if (isBlockedElement(el)) {
    return {
      type: "change",
      ts: now(),
      target: "[blocked]",
      path: currentPath(),
      text: "",
      meta: { blocked: true },
    }
  }

  const { target, path } = base(el)
  const isMasked = isMaskedElement(el)
  const isInput = el instanceof HTMLInputElement && el.type !== "checkbox" && el.type !== "radio"
  const value = el?.value ?? ""
  return {
    type: "change",
    ts: now(),
    target,
    path,
    text: (isMasked || isInput) ? "•••" : sanitizeText(value, isMasked).slice(0, 100),
    meta: {
      tag: el?.tagName?.toLowerCase() || "",
      name: el?.name || "",
    },
  }
}

export function captureSubmit(form: HTMLFormElement | null): SessionEvent {
  if (isBlockedElement(form)) {
    return {
      type: "form_submit",
      ts: now(),
      target: "[blocked]",
      path: currentPath(),
      text: "",
      meta: { blocked: true },
    }
  }

  const { target, path } = base(form)
  const action = form?.action || ""
  return {
    type: "form_submit",
    ts: now(),
    target: target || "form",
    path,
    text: form?.id || form?.name || action.split("?")[0] || "form",
    meta: {
      action: action.split("?")[0],
      method: form?.method || "POST",
    },
  }
}

export function capturePageView(): SessionEvent {
  return {
    type: "page_view",
    ts: now(),
    target: "",
    path: currentPath(),
    text: typeof document !== "undefined" ? document.title : "",
    meta: {
      title: typeof document !== "undefined" ? document.title : "",
      href: typeof window !== "undefined" ? window.location.href.split("?")[0] : "",
    },
  }
}

export function captureConsole(level: "error" | "warn" | "info" | "log", message: string): SessionEvent {
  return {
    type: "console",
    ts: now(),
    target: "console",
    path: currentPath(),
    text: sanitizeText(message).slice(0, 500),
    meta: { level },
  }
}

export function captureError(
  error: Error | ErrorEvent | PromiseRejectionEvent | string,
  errorObj?: unknown,
  details?: { filename?: string; lineno?: number; colno?: number },
): SessionEvent {
  let message = "Unknown error"
  let stack = ""
  let errName = "Error"

  if (typeof error === "string") {
    message = error
    if (errorObj instanceof Error) {
      stack = errorObj.stack || ""
      errName = errorObj.name || "Error"
      if (!message) message = errorObj.message
    }
  } else if (error instanceof Error) {
    message = error.message
    stack = error.stack || ""
    errName = error.name || "Error"
  } else if (typeof ErrorEvent !== "undefined" && error instanceof ErrorEvent) {
    message = error.message
    if (error.error instanceof Error) {
      stack = error.error.stack || ""
      errName = error.error.name || "Error"
    } else {
      stack = (error.error as Error | undefined)?.stack || ""
    }
  } else if (typeof PromiseRejectionEvent !== "undefined" && error instanceof PromiseRejectionEvent) {
    const reason = (error as PromiseRejectionEvent).reason
    if (reason instanceof Error) {
      message = reason.message || String(reason)
      stack = reason.stack || ""
      errName = reason.name || "Error"
    } else {
      message = String(reason)
    }
  }

  // window.onerror supplies filename/lineno/colno even when `error` is
  // undefined (cross-origin scripts, `throw "str"`). Preserve them so the
  // backend / issue page can still resolve a code snippet.
  const filename = details?.filename || ""
  const lineno = typeof details?.lineno === "number" ? details.lineno : 0
  const colno = typeof details?.colno === "number" ? details.colno : 0

  // Extract the first usable frame from the raw stack as a structured
  // hint (file_path/line_number). Full stack stays in exception.stacktrace.
  let topFile = filename
  let topLine = lineno || 0
  let topCol = colno || 0
  if (stack) {
    const v8 = stack.match(/at\s+(?:.*?\s+\()?(.+?):(\d+)(?::(\d+))?\)?/)
    const py = stack.match(/File\s+"([^"]+)",\s+line\s+(\d+)/)
    if (v8 && (!topFile || topFile === "")) {
      topFile = v8[1]
      topLine = parseInt(v8[2], 10) || topLine
      topCol = v8[3] ? parseInt(v8[3], 10) : topCol
    } else if (py && (!topFile || topFile === "")) {
      topFile = py[1]
      topLine = parseInt(py[2], 10) || topLine
    }
  }

  const meta: Record<string, string | number | boolean> = {
    message: sanitizeText(message).slice(0, 500),
    stack: stack.slice(0, 2000),
    // OTel-style keys so backend trace lookups (`exception.stacktrace`)
    // and session-event fallbacks share one convention.
    "exception.message": sanitizeText(message).slice(0, 500),
    "exception.type": errName,
    "exception.stacktrace": stack.slice(0, 8000),
  }
  if (topFile) meta["file_path"] = String(topFile).slice(0, 500)
  if (topLine) meta["line_number"] = topLine
  if (topCol) meta["column"] = topCol

  return {
    type: "error",
    ts: now(),
    target: "window.onerror",
    path: currentPath(),
    text: sanitizeText(message).slice(0, 300),
    meta,
  }
}
